import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, AlertCircle, Trash2, Wrench, TrendingDown, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { dataService } from '@/services/data-service';
import type { Asset } from '@/types/asset';

// Mock forecast data - Apr 2025 to Mar 2026
const forecastData = [
  { month: 'Apr 25', historical: 500000, forecasted: null },
  { month: 'May 25', historical: 485000, forecasted: null },
  { month: 'Jun 25', historical: 475000, forecasted: null },
  { month: 'Jul 25', historical: 470000, forecasted: null },
  { month: 'Aug 25', historical: 465000, forecasted: null },
  { month: 'Sep 25', historical: 460000, forecasted: null },
  { month: 'Oct 25', historical: 455000, forecasted: 455000 },
  { month: 'Nov 25', historical: null, forecasted: 448000 },
  { month: 'Dec 25', historical: null, forecasted: 441000 },
  { month: 'Jan 26', historical: null, forecasted: 434000 },
  { month: 'Feb 26', historical: null, forecasted: 427000 },
  { month: 'Mar 26', historical: null, forecasted: 420000 },
];

// Type for disposal recommendation with calculated percentage
interface DisposalRecommendation extends Asset {
  percentOfAcquisitionValue: number;
}

const insightCards = [
  {
    title: 'Assets Requiring Attention',
    value: '8',
    change: '+2',
    trend: 'up',
    icon: AlertCircle,
    description: 'from last month',
  },
  {
    title: 'Upcoming Disposals',
    value: '3',
    change: '+1',
    trend: 'up',
    icon: Trash2,
    description: 'from last month',
  },
  {
    title: 'Maintenance Due',
    value: '5',
    change: '0',
    trend: 'neutral',
    icon: Wrench,
    description: 'same as last month',
  },
  {
    title: 'Underutilized Assets',
    value: '12',
    change: '-3',
    trend: 'down',
    icon: TrendingDown,
    description: 'from last month',
  },
];

const forecastingInsights = [
  {
    title: 'Depreciation Trend',
    description: 'Current depreciation rate is 15% higher than industry average. Consider reviewing depreciation methods.',
  },
  {
    title: 'Asset Lifecycle',
    description: '23% of computer equipment is approaching end of useful life within the next 6 months.',
  },
  {
    title: 'Optimization Opportunity',
    description: 'Consolidating underutilized assets could save approximately £45,000 annually.',
  },
  {
    title: 'Maintenance Costs',
    description: 'Maintenance costs are increasing 8% year-over-year. Consider replacement strategy for aging assets.',
  },
];

export default function InsightsPage(): React.ReactElement {
  const navigate = useNavigate();
  const [currentTotalNBV, setCurrentTotalNBV] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [disposalRecommendations, setDisposalRecommendations] = useState<DisposalRecommendation[]>([]);
  const [isLoadingDisposals, setIsLoadingDisposals] = useState<boolean>(true);

  // Fixed depreciation value
  const EXPECTED_DEPRECIATION = 35000;

  // Calculate projected NBV
  const projectedNBV = currentTotalNBV - EXPECTED_DEPRECIATION;

  useEffect(() => {
    const fetchKPIs = async () => {
      try {
        setIsLoading(true);
        const kpis = await dataService.getDashboardKPIs();
        
        // Find the Total Asset Value KPI which contains the sum of all NBV
        const totalAssetValueKPI = kpis.find((kpi: any) => kpi.label === 'Total Asset Value');
        
        if (totalAssetValueKPI && typeof totalAssetValueKPI.value === 'number') {
          setCurrentTotalNBV(totalAssetValueKPI.value);
        }
      } catch (error) {
        console.error('Error fetching KPIs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchKPIs();
  }, []);

  useEffect(() => {
    const fetchDisposalRecommendations = async () => {
      try {
        setIsLoadingDisposals(true);
        
        // Fetch all assets (with a large page size to get all assets)
        const response = await dataService.getAssets({ pageSize: 10000 });
        
        // Filter assets where % of Acquisition Value ≤ 10%
        const eligibleAssets = response.data
          .filter((asset: any) => {
            const percentOfAcquisitionValue = (asset.netBookValue / asset.acquisitionCost) * 100;
            return percentOfAcquisitionValue <= 10;
          })
          .map((asset: any) => ({
            ...asset,
            percentOfAcquisitionValue: Math.round((asset.netBookValue / asset.acquisitionCost) * 100)
          }));
        
        // Randomly select 3 assets
        const shuffled = [...eligibleAssets].sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, 3);
        
        setDisposalRecommendations(selected);
      } catch (error) {
        console.error('Error fetching disposal recommendations:', error);
      } finally {
        setIsLoadingDisposals(false);
      }
    };

    fetchDisposalRecommendations();
  }, []);

  const handleAssetsRequiringAttentionClick = () => {
    // Navigate to assets page with filter parameter
    navigate('/assets?filter=requiring-attention');
  };

  // Format currency helper
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Page Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              Insights
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              AI-powered insights and forecasting based on your asset data
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="space-y-6 p-6">
      {/* Key Insights Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {insightCards.map((card) => {
          const Icon = card.icon;
          const TrendIcon = card.trend === 'up' ? ArrowUpRight : card.trend === 'down' ? ArrowDownRight : null;
          const isAssetsRequiringAttention = card.title === 'Assets Requiring Attention';
          
          return (
            <Card 
              key={card.title}
              className={`relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300 ${
                isAssetsRequiringAttention ? 'cursor-pointer hover:border-blue-400' : ''
              }`}
              onClick={isAssetsRequiringAttention ? handleAssetsRequiringAttentionClick : undefined}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
              <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-semibold tracking-tight">{card.title}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="relative">
                <div className="text-2xl font-bold tracking-tight">{card.value}</div>
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  {TrendIcon && (
                    <TrendIcon
                      className={`h-3 w-3 ${
                        card.trend === 'up'
                          ? 'text-red-500'
                          : card.trend === 'down'
                          ? 'text-green-500'
                          : 'text-muted-foreground'
                      }`}
                    />
                  )}
                  <span
                    className={
                      card.trend === 'up'
                        ? 'text-red-500'
                        : card.trend === 'down'
                        ? 'text-green-500'
                        : 'text-muted-foreground'
                    }
                  >
                    {card.change}
                  </span>{' '}
                  {card.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Asset Value Forecast */}
      <Card className="relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="text-base font-semibold tracking-tight">Asset Value Forecast</CardTitle>
          <CardDescription>Predicted net book value based on current depreciation rates</CardDescription>
        </CardHeader>
        <CardContent className="relative">
          <div className="mb-4 grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border bg-muted/50 px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground">Current Total NBV</p>
              <p className="text-2xl font-bold tracking-tight">
                {isLoading ? 'Loading...' : formatCurrency(currentTotalNBV)}
              </p>
            </div>
            <div className="rounded-lg border bg-muted/50 px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground">Projected NBV (12 months)</p>
              <p className="text-2xl font-bold tracking-tight">
                {isLoading ? 'Loading...' : formatCurrency(projectedNBV)}
              </p>
            </div>
            <div className="rounded-lg border bg-muted/50 px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground">Expected Depreciation</p>
              <p className="text-2xl font-bold tracking-tight text-red-600">
                {isLoading ? 'Loading...' : formatCurrency(-EXPECTED_DEPRECIATION)}
              </p>
            </div>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={forecastData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis
                  tickFormatter={(value) =>
                    new Intl.NumberFormat('en-GB', {
                      style: 'currency',
                      currency: 'GBP',
                      notation: 'compact',
                      maximumFractionDigits: 1,
                    }).format(value)
                  }
                />
                <RechartsTooltip
                  formatter={(value: number) =>
                    new Intl.NumberFormat('en-GB', {
                      style: 'currency',
                      currency: 'GBP',
                    }).format(value)
                  }
                />
                <Line type="monotone" dataKey="historical" stroke="#3b82f6" strokeWidth={2} name="Historical" connectNulls={false} />
                <Line type="monotone" dataKey="forecasted" stroke="#ef4444" strokeWidth={2} name="Forecasted" connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Disposal Recommendations */}
        <Card className="relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="text-base font-semibold tracking-tight">Disposal Recommendations</CardTitle>
            <CardDescription>Assets that should be considered for disposal</CardDescription>
          </CardHeader>
          <CardContent className="relative max-h-[400px] overflow-y-auto space-y-3">
            {isLoadingDisposals ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Loading disposal recommendations...
              </p>
            ) : disposalRecommendations.length > 0 ? (
              disposalRecommendations.map((asset) => (
                <div 
                  key={asset.id} 
                  onClick={() => navigate(`/assets?id=${asset.id}`)}
                  className="rounded-lg border bg-background/50 p-4 shadow-sm hover:shadow-md hover:border-primary/50 transition-all duration-200 cursor-pointer group"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-left group-hover:text-primary transition-colors">
                        {asset.name}
                      </span>
                      <Badge 
                        variant={asset.percentOfAcquisitionValue === 0 ? "destructive" : "secondary"}
                        className={asset.percentOfAcquisitionValue === 0 ? "bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200" : "bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-200"}
                      >
                        {asset.percentOfAcquisitionValue === 0 ? "Recommend Disposal" : "Consider Disposal"}
                      </Badge>
                    </div>
                    <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors block">
                      {asset.assetId}
                    </span>
                    <div className="space-y-1 pt-2">
                      <p className="text-sm font-medium">
                        Current NBV:{' '}
                        {new Intl.NumberFormat('en-GB', {
                          style: 'currency',
                          currency: 'GBP',
                        }).format(asset.netBookValue)}
                      </p>
                      <p className="text-sm font-medium">
                        Acquisition Value:{' '}
                        {new Intl.NumberFormat('en-GB', {
                          style: 'currency',
                          currency: 'GBP',
                        }).format(asset.acquisitionCost)}
                      </p>
                      <p className="text-sm font-medium text-red-600">
                        % of Acquisition Value: {asset.percentOfAcquisitionValue}%
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No assets currently meet the disposal criteria (≤ 10% of acquisition value)
              </p>
            )}
          </CardContent>
        </Card>

        {/* Forecasting Insights */}
        <Card className="relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="text-base font-semibold tracking-tight">Forecasting Insights</CardTitle>
            <CardDescription>AI-generated insights based on your asset data</CardDescription>
          </CardHeader>
          <CardContent className="relative space-y-4">
            {forecastingInsights.map((insight, index) => (
              <div key={index} className="rounded-lg border bg-background/50 p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <Sparkles className="mt-0.5 h-4 w-4 text-purple-600 dark:text-purple-400" />
                  <div className="space-y-1">
                    <p className="font-semibold">{insight.title}</p>
                    <p className="text-sm text-muted-foreground">{insight.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
        </div>
      </div>
    </div>
  );
}
